// "Create list" and "Add to list…" for a set of selected claims. Used on the Claims page,
// the A/R page's patient claims and the Waterfall bucket drill-down. The title comes from
// a prompt; "Add to list…" offers the practice's OPEN lists. Both report back through a
// dismissable message with a link to the list.
import React, { useEffect, useRef, useState } from "react";
import "./ClaimLists.css";
import {
  createClaimList,
  fetchClaimLists,
  listPath,
  updateClaimList,
} from "./api";

export default function ClaimListActions({
  practiceId,
  selectedIds,
  source = "manual",
  defaultTitle = "",
  onDone,
  onOpenList,
}) {
  const ids = selectedIds || [];
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openLists, setOpenLists] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const done = (list, text) => {
    setMessage({ type: "success", text, list });
    if (onDone) onDone(list);
  };

  const create = async () => {
    if (!ids.length) return;
    const title = window.prompt("Name this list", defaultTitle);
    if (title === null) return;
    if (!title.trim()) {
      setMessage({ type: "error", text: "A list needs a title." });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const list = await createClaimList(practiceId, {
        title: title.trim(),
        claimIds: ids,
        source,
      });
      done(list, `Created "${list.title}" with ${list.count} claim${list.count === 1 ? "" : "s"}.`);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleMenu = async () => {
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    setMenuOpen(true);
    setOpenLists(null);
    try {
      const lists = await fetchClaimLists(practiceId);
      setOpenLists(lists.filter((l) => l.status === "open"));
    } catch (err) {
      setMenuOpen(false);
      setMessage({ type: "error", text: err.message });
    }
  };

  const addTo = async (list) => {
    setMenuOpen(false);
    setBusy(true);
    setMessage(null);
    try {
      const updated = await updateClaimList(practiceId, list.id, { add_claim_ids: ids });
      const added = updated.count - list.count;
      done(
        updated,
        `Added ${added} claim${added === 1 ? "" : "s"} to "${updated.title}" (${updated.count} in total).`,
      );
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const openLink = (e, list) => {
    if (!onOpenList) return;
    e.preventDefault();
    onOpenList(list.id);
  };

  return (
    <div className="claim-list-actions" data-testid="claim-list-actions">
      <button
        type="button"
        className="btn btn-secondary"
        disabled={busy || ids.length === 0}
        onClick={create}
      >
        Create list{ids.length ? ` (${ids.length})` : ""}
      </button>
      <div className="claim-list-menu" ref={menuRef}>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy || ids.length === 0}
          onClick={toggleMenu}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          Add to list…
        </button>
        {menuOpen && (
          <div className="claim-list-menu-panel" role="menu">
            {openLists === null ? (
              <div className="claim-list-menu-empty">Loading lists…</div>
            ) : openLists.length === 0 ? (
              <div className="claim-list-menu-empty">No open lists yet.</div>
            ) : (
              openLists.map((l) => (
                <button
                  type="button"
                  role="menuitem"
                  key={l.id}
                  className="claim-list-menu-item"
                  onClick={() => addTo(l)}
                >
                  <span>{l.title}</span>
                  <span className="claim-list-count">{l.count}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {message && (
        <div className={`claim-list-message ${message.type}`}>
          <span>
            {message.text}
            {message.list ? (
              <>
                {" "}
                <a
                  href={listPath(practiceId, message.list.id)}
                  onClick={(e) => openLink(e, message.list)}
                >
                  Open list
                </a>
              </>
            ) : null}
          </span>
          <button
            type="button"
            className="message-close"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
